import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import { useForm } from "@context/FormContext";
import InputField from "@components/form//InputField";
import StepHeader from "@components/form-builder/StepHeader";
import EditableSelect from "@components/form/EditableSelect";
import FormStepper from "@components/form-builder/FormStepper";
import { questionTypes } from "@constants/questionTypes";

export default function BasicInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formData, updateFormData, setTabs } = useForm();

  const isValid = formData.formName.trim() !== "";

  useEffect(() => {
    const formToEdit = location.state?.formToEdit;
    if (formToEdit) {
      updateFormData("id", formToEdit.id);
      updateFormData("formName", formToEdit.name || "");
      updateFormData("canFillAtHome", formToEdit.fillable_at_home);
      if (formToEdit.structure) {
        const parsedContent =
          typeof formToEdit.structure === "string"
            ? JSON.parse(formToEdit.structure)
            : formToEdit.structure;
        const iconMap = questionTypes.reduce((acc, { label, icon }) => {
          acc[label] = icon;
          return acc;
        }, {});

        // Inject icons
        const enrichedTabs = parsedContent.map((tab) => ({
          ...tab,
          content: tab.content.map((q) => ({
            ...q,
            icon: iconMap[q.select] || null,
          })),
        }));

        setTabs(enrichedTabs);
      }
    }
  }, [location.state]);
  return (
    <div className="min-h-screen flex flex-col">
      <div className="sticky top-0 z-50">
        <Navbar showFieldSearch={false} />
      </div>
      <div className="flex-grow mt-4 px-4 flex flex-col">
        <form
          className="flex flex-col flex-grow p-5"
          onSubmit={(e) => {
            e.preventDefault(); // prevent page reload
            if (isValid) navigate("/form-questions");
          }}
        >
          <StepHeader title="Create New Form" onClose={() => navigate("/")} />
          <FormStepper currentStep={1} />

          <div className="space-y-9 flex-grow">
            <InputField
              label="Form Name"
              required
              value={formData.formName}
              onChange={(e) => updateFormData("formName", e.target.value)}
              placeholder="(e.g. Medical Consent Form)"
            />

            <div className="gap-2 space-y-4">
              <p className="text-sm font-medium text-gray-900">
                Can be filled by patient at home?
              </p>
              <label className="font-normal italic text-gray-500">
                Enable this if you want to allow patients to fill out the form
                remotely before visiting the clinic.
              </label>

              <div className="flex gap-12">
                {[
                  { label: "No", value: false },
                  { label: "Yes", value: true },
                ].map(({ label, value }) => (
                  <div className="flex items-center me-4" key={label}>
                    <input
                      type="radio"
                      id={`${label}-radio`}
                      name="canFillAtHome"
                      value={String(value)}
                      checked={formData.canFillAtHome === value}
                      onChange={() => updateFormData("canFillAtHome", value)}
                      className="w-4 h-4 text-blue-600 bg-gray-50 border-gray-300"
                    />
                    <label
                      htmlFor={`${label}-radio`}
                      className="ms-2 text-sm font-medium"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ✅ Stick this to bottom */}
          <div className="pt-6 mt-10">
            <button
              type="submit"
              disabled={!isValid}
              className={`w-full text-base font-medium py-2 px-4 rounded-lg text-white ${
                isValid
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
